import Api from '@/services/Api';
import { base } from '@/services';
import type { State } from './state';
import type { MyLibraryItemUpdate } from './types';
import { mutations as MUTATION, actions as ACTION } from './types';
import { libraryServices } from '../services';
import eventService from '@/services/EventService';
import { EVENT_TYPES } from '@/services';

type ActionArg = {
  commit: Function;
  state: State;
};

const saveListLibrary = (libraries: any, list: any, cachedSavedList: any, type: any) => {
  const cachedList: { id: any }[] = [];
  for (const lib of libraries) {
    if (!list.find((e: { id: any }) => e.id === lib.id)) {
      cachedList.push(lib);
      if (type && type !== 'all' && type !== '') cachedSavedList.push(lib);
    }
  }
  list.push(...cachedList);
};

const actions = {
  [ACTION.GET_TEMPLATE_DATA]({ state, commit }: ActionArg, pageType: string) {
    return new Promise(async (resolve, reject) => {
      if (state.fetchedTab.template) {
        resolve(state.template);
        return state.template;
      }
      try {
        if (!state.fetchedTab.templateTags) {
          const [templateRes, { data: tagRes }] = await Promise.all([
            Api.base.get(base.getTemplates),
            Api.base.get(base.getTemplateTags),
          ]);
          commit(MUTATION.COMMIT_TEMPLATE_DATA, templateRes.data);
          commit(MUTATION.COMMIT_TEMPLATE_TAG_DATA, {
            tags: tagRes.data,
            templates: templateRes.data?.templates,
            pageType,
          });
          resolve(templateRes.data);
        } else {
          const templateRes = await Api.base.get(base.getTemplates);
          commit(MUTATION.COMMIT_TEMPLATE_DATA, templateRes.data);
          commit(MUTATION.COMMIT_TEMPLATE_TAG_DATA, {
            tags: state.templateTags,
            templates: templateRes.data?.templates,
            pageType,
          });
          resolve(templateRes.data);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        reject(e);
      }
    });
  },
  [ACTION.GET_BLOCK_DATA]({ state, commit }: ActionArg, filterParams = { refresh: false, loadMore: false }) {
    return new Promise(async (resolve, reject) => {
      const { period, data } = state.blocks;
      try {
        commit(MUTATION.UPDATE_BLOCK_PERIOD, {
          ...period,
          refresh: filterParams.refresh || false,
          loadMore: filterParams.loadMore || false,
        });
        const page = filterParams.refresh ? 1 : period.page;

        if (!state.fetchedTab.templateTags) {
          const [blockRes, { data: tagRes }, blocksCountTagsResponse] = await Promise.all([
            libraryServices.apiGetBlocks(page, period.pageSize, filterParams),
            Api.base.get(base.getTemplateTags),
            libraryServices.apiGetBlockCountTags(),
          ]);
          commit(MUTATION.COMMIT_BLOCK_DATA, {
            period: {
              ...period,
              page: page + 1,
              limited: blockRes.data?.blocks?.length < period.pageSize,
              refresh: false,
              loadMore: false,
            },
            data: filterParams.refresh ? blockRes.data?.blocks : data.concat(blockRes.data?.blocks),
          });

          commit(MUTATION.COMMIT_BLOCK_TAG_DATA, {
            tags: tagRes.data,
            blockUseCase: blocksCountTagsResponse.data.tags,
          });
          resolve(blockRes.data);
        } else {
          const blockUseCase = state.blockUseCase;
          const blockRes = await libraryServices.apiGetBlocks(page, period.pageSize, filterParams);
          commit(MUTATION.COMMIT_BLOCK_DATA, {
            period: {
              ...period,
              page: page + 1,
              limited: blockRes.data?.blocks?.length < period.pageSize,
              refresh: false,
              loadMore: false,
            },
            data: filterParams.refresh ? blockRes.data?.blocks : data.concat(blockRes.data?.blocks),
          });
          commit(MUTATION.COMMIT_BLOCK_TAG_DATA, {
            tags: state.templateTags,
            blockUseCase,
          });
          resolve(blockRes.data);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        commit(MUTATION.COMMIT_BLOCK_DATA, {
          period: {
            ...period,

            limited: true,
            refresh: false,
            loadMore: false,
          },
          data: data,
        });
        reject(e);
      }
    });
  },
  [ACTION.GET_MYLIBRARY_DATA](
    { state }: ActionArg,
    params: { offset: number; query: string; type: string; counter: boolean },
  ) {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await Api.base.get(base.getMyLibrary, { params: { ...params, limit: 30 } });
        const { list, queriedList, cachedSavedList } = state.mylibrary;
        const { libraries, total } = res.data;

        if (params.counter && total) {
          state.mylibrary.allSaved = total.block + total.template;
          state.mylibrary.allBlocks = total.block;
          state.mylibrary.allTemplates = total.template;
        }

        if (params.query && params.query.length != 0) {
          if (libraries.length == 0) state.mylibrary.fetchedAllQuery = true;
          saveListLibrary(libraries, queriedList, null, null);
        } else if (!params.query) {
          if (libraries.length == 0) state.mylibrary.fetchedAll = true;
          saveListLibrary(libraries, list, cachedSavedList, params.type);
        }

        // console.log("state", state.mylibrary.fetchedAll);
        resolve(res.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        reject(e);
      }
    });
  },
  [ACTION.GET_ELEMENT_DATA]({ state, commit }: ActionArg) {
    return new Promise(async (resolve, reject) => {
      if (state.fetchedTab.element) {
        resolve(state.element);
        return state.element;
      }
      try {
        const res = await Api.base.get(base.getElements, { params: { limit: 500 } });
        commit(MUTATION.COMMIT_ELEMENT_DATA, res.data);
        resolve(res.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        reject(e);
      }
    });
  },
  [ACTION.INSTALL_ELEMENT]({ state, commit }: ActionArg, data: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await Api.base.post(base.elementCRUD(data.id), data);
        const commitData = {
          id: data.id,
          data: {
            installed: true,
            removable: true,
            // eslint-disable-next-line @typescript-eslint/camelcase, camelcase
            available_update: false,
          },
        };
        commit(MUTATION.MUTATE_ELEMENT_ITEM, commitData);
        resolve(res.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        reject(e);
      }
    });
  },
  [ACTION.UNINSTALL_ELEMENT]({ state, commit }: ActionArg, data: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await Api.base.delete(base.elementCRUD(data.id), data);
        // commit(MUTATION.UNINSTALL_ELEMENT, data);
        const commitData = {
          id: data.id,
          data: {
            installed: false,
            removable: false,
            // eslint-disable-next-line @typescript-eslint/camelcase, camelcase
            available_update: false,
          },
        };
        commit(MUTATION.MUTATE_ELEMENT_ITEM, commitData);
        resolve(res.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        reject(e);
      }
    });
  },
  [ACTION.UPDATE_ELEMENT]({ state, commit }: ActionArg, data: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await Api.base.put(base.updateElement(data.id), data);
        const commitData = {
          id: data.id,
          data: {
            installed: true,
            removable: true,
            // eslint-disable-next-line @typescript-eslint/camelcase, camelcase
            available_update: false,
          },
        };
        commit(MUTATION.MUTATE_ELEMENT_ITEM, commitData);
        resolve(res.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        reject(e);
      }
    });
  },

  [ACTION.GET_TEMPLATE]({ state, commit }: ActionArg, { id, global }: { id: number; global: boolean }) {
    return new Promise(async (resolve, reject) => {
      const params: any = {
        templateId: id,
      };
      if (global) {
        params.global = true;
      }
      try {
        const res = await Api.base.get(base.getTemplate, { params });
        resolve(res.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        reject(e);
      }
    });
  },
  [ACTION.GET_BLOCK]({ state, commit }: ActionArg, { id, global }: { id: number; global: boolean }) {
    return new Promise(async (resolve, reject) => {
      const params: any = {
        blockId: id,
      };
      if (global) {
        params.global = true;
      }
      try {
        const res = await Api.base.get(base.getBlock, { params });
        resolve(res.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        reject(e);
      }
    });
  },
  [ACTION.DELETE_MYLIBRARY_ITEM]({ state, commit }: ActionArg, { id, type }: { id: number; type: string }) {
    return new Promise(async (resolve, reject) => {
      const params = {
        id,
      };
      try {
        let url = base.removeMyBlock;
        if (type == 'template') {
          url = base.removeMyTemplate;
        }
        const res = await Api.base.post(url, params);
        if (res?.data?.status == 'success') {
          commit(MUTATION.COMMIT_DELETE_MYLIBRARY_ITEM, { id, type });
          resolve(res.data);
        } else {
          reject(res.data);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        reject(err);
      }
    });
  },
  [ACTION.UPDATE_MYLIBRARY_ITEM](
    { state, commit }: ActionArg,
    { id, type, fields }: { id: number; type: string; fields: MyLibraryItemUpdate },
  ) {
    return new Promise(async (resolve, reject) => {
      const params = {
        [type]: fields,
      };
      try {
        let url = base.updateMyBlock(id);
        if (type == 'template') {
          url = base.updateMyTemplate(id);
        }
        const res = await Api.base.put(url, params);
        if (res?.data?.status == 'success') {
          commit(MUTATION.COMMIT_UPDATE_MYLIBRARY_ITEM, { id, type, fields });
          resolve(res.data);
        } else {
          reject(res.data);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        reject(err);
      }
    });
  },
  [ACTION.CHECK_MYLIBRARY_SHARE]({ state, commit }: ActionArg) {
    return new Promise(async (resolve, reject) => {
      if (state.mylibrary.shared.fetched) {
        resolve(state.mylibrary.shared);
        return state.mylibrary.shared;
      }
      try {
        const res = await Api.base.get(base.checkShareLibrary);
        commit(MUTATION.COMMIT_MYLIBRARY_SHARE_DATA, res.data);
        resolve(res.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        reject(e);
      }
    });
  },
  [ACTION.GET_COUNT_ADD_ON_LIBRARY]({ state, commit }: ActionArg) {
    const { countBy } = eventService();
    return new Promise(async (resolve, reject) => {
      try {
        const res = await countBy({
          event_type: EVENT_TYPES.library,
        });
        commit(MUTATION.NUMBER_OF_USE_ADD_TO_PAGE, res.total);
        resolve(res.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        reject(e);
      }
    });
  },
  [ACTION.DO_SHARE_MYLIBRARY]({ state, commit }: ActionArg, data: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await Api.base.post(base.shareLibrary, data);
        resolve(res.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        reject(e);
      }
    });
  },
  [ACTION.CREATE_LIB](
    { state, commit }: ActionArg,
    { data, type, global }: { data: any; type: string; global: boolean },
  ) {
    return new Promise(async (resolve, reject) => {
      try {
        let url = base.createMyBlock;
        if (type == 'template') {
          url = base.createMyTemplate;
        }
        const res = await Api.base.post(url, data);
        commit(MUTATION.COMMIT_MYLIBRARY_LIB, { data: res.data, type, global });
        resolve(res.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        reject(e);
      }
    });
  },
};

export default actions;
